import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Trash2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TeamForm } from "@/components/TeamForm";
import { SharePanel } from "@/components/SharePanel";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CoachesPanel } from "@/components/CoachesPanel";

const FIELDS = ["name", "age_group", "sport", "format", "player_count", "colors", "offense_positions", "defense_positions"];

export default function TeamSettings() {
  const { teamId } = useParams();
  const nav = useNavigate();
  const { loadTeams } = useAuth();
  const [team, setTeam] = useState(null);
  const [form, setForm] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/teams/${teamId}`).then(({ data }) => { setTeam(data); setForm(Object.fromEntries(FIELDS.map((k) => [k, data[k]]))); })
      .catch((e) => { toast.error(errMsg(e)); nav("/teams"); });
  }, [teamId, nav]);

  if (!team) return <p className="text-slate-400" data-testid="team-settings-loading">Loading…</p>;

  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/teams/${teamId}`, form); setTeam(data); loadTeams(); toast.success("Team settings saved"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { await api.delete(`/teams/${teamId}`); await loadTeams(); toast.success("Team deleted"); nav("/teams"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="max-w-3xl">
      <Link to="/teams" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white mb-3" data-testid="back-to-teams"><ArrowLeft size={16} />All teams</Link>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div><p className="label">Team settings</p><h1 className="h1" data-testid="team-settings-title">{team.name}</h1></div>
        <Link to={`/teams/${teamId}/playbook`} className="btn btn-ghost" data-testid="settings-open-playbook"><BookOpen size={18} />Playbook</Link>
      </div>
      {team.can_manage ? (
      <section className="card p-5 sm:p-6">
        <h2 className="h2 mb-4">Team details</h2>
        <TeamForm value={form} onChange={setForm} showLabels />
        <p className="text-xs text-slate-500 mt-4">Leagues differ. Changing players per side affects new plays; existing plays keep their players.</p>
        <button className="btn btn-primary mt-5" onClick={save} disabled={busy || !form.name.trim()} data-testid="team-settings-save"><Save size={18} />{busy ? "Saving…" : "Save settings"}</button>
      </section>
      ) : (
      <section className="card p-5 sm:p-6" data-testid="team-readonly-summary">
        <h2 className="h2 mb-2">Team details</h2>
        <p className="text-slate-300">{[team.age_group, team.format === "flag" ? "Flag" : "Tackle", `${team.player_count} per side`].filter(Boolean).join(" · ")}</p>
        <p className="text-xs text-slate-500 mt-2">You're an assistant coach. The head coach manages team settings and sharing.</p>
      </section>
      )}
      <section className="card p-5 sm:p-6 mt-6">
        <h2 className="h2 mb-4">Coaches</h2>
        <CoachesPanel team={team} />
      </section>
      {team.can_manage && (<>
      <section className="card p-5 sm:p-6 mt-6">
        <h2 className="h2 mb-4">Share with your team</h2>
        <SharePanel team={team} onChange={setTeam} />
      </section>
      <section className="card p-5 sm:p-6 mt-6 border-red-500/30">
        <h2 className="h2 mb-2 text-red-300">Delete team</h2>
        <p className="text-sm text-slate-400 mb-4">Deletes the team and every play in its playbook. This can't be undone.</p>
        <button className="btn btn-danger" onClick={() => setConfirm(true)} data-testid="btn-delete-team"><Trash2 size={18} />Delete team</button>
      </section>
      </>)}
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title="Delete this team?" body={`"${team.name}" and all of its plays will be removed for good.`} confirmLabel="Delete team" onConfirm={remove} testId="confirm-delete-team" />
    </div>
  );
}
